import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  fsGetHabits,
  fsSaveHabit,
  fsDeleteHabit,
  fsGetAllHabitLogs,
  fsToggleHabitLog,
  fsGetStreakFreeze,
  fsSaveStreakFreeze,
} from '../services/firestoreDb';
import { computeStreaks } from '../utils/streak';
import { localDateKey, localDateKeys } from '../utils/dateKey';

const DEFAULT_FREEZE_STATE = { freezesAvailable: 2, frozenDates: [] };

function storageScope(uid) {
  return uid ? `user_${uid}` : 'guest';
}

function localGet(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

function localSet(key, value) {
  localStorage.setItem(key, JSON.stringify(value));
}

const habitsKey = (uid) => `sm_habits_${storageScope(uid)}`;
const logsKey = (uid) => `sm_habit_logs_${storageScope(uid)}`;
const freezeKey = (uid) => `sm_streak_freeze_${storageScope(uid)}`;

function shiftedDateKey(days) {
  return localDateKey(new Date(Date.now() + days * 86400000));
}

export function useHabits() {
  const { user } = useAuth();
  const uid = user?.id;

  const [habits, setHabits] = useState(() => localGet(habitsKey(null), []));
  const [logsByDate, setLogsByDate] = useState(() => localGet(logsKey(null), {}));
  const [freezeState, setFreezeState] = useState(() => localGet(freezeKey(null), DEFAULT_FREEZE_STATE));
  const [loaded, setLoaded] = useState(false);
  const writeQueue = useRef(Promise.resolve());

  useEffect(() => {
    let active = true;
    setHabits(localGet(habitsKey(uid), []));
    setLogsByDate(localGet(logsKey(uid), {}));
    setFreezeState(localGet(freezeKey(uid), DEFAULT_FREEZE_STATE));
    setLoaded(!uid);

    if (!uid) return () => { active = false; };

    Promise.all([fsGetHabits(uid), fsGetAllHabitLogs(uid), fsGetStreakFreeze(uid)])
      .then(async ([remoteHabits, remoteLogs, remoteFreeze]) => {
        if (!active) return;
        const nextHabits = remoteHabits || [];
        const nextLogsByDate = {};
        (remoteLogs || []).forEach(({ date, ...log }) => {
          nextLogsByDate[date] = log;
        });
        let nextFreeze = remoteFreeze || DEFAULT_FREEZE_STATE;

        // Auto-apply a streak freeze if a habit's chain would otherwise break
        // because yesterday was missed but the day before was completed.
        const y = shiftedDateKey(-1);
        const dayBefore = shiftedDateKey(-2);
        if (nextFreeze.freezesAvailable > 0 && !nextFreeze.frozenDates.includes(y)) {
          const atRisk = nextHabits.some((h) => {
            const completedYesterday = nextLogsByDate[y]?.[h.id];
            const completedDayBefore = nextLogsByDate[dayBefore]?.[h.id];
            return !completedYesterday && completedDayBefore;
          });
          if (atRisk) {
            nextFreeze = {
              freezesAvailable: nextFreeze.freezesAvailable - 1,
              frozenDates: [...nextFreeze.frozenDates, y].slice(-60),
            };
            await fsSaveStreakFreeze(uid, nextFreeze).catch(() => {});
          }
        }

        setHabits(nextHabits);
        setLogsByDate(nextLogsByDate);
        setFreezeState(nextFreeze);
        localSet(habitsKey(uid), nextHabits);
        localSet(logsKey(uid), nextLogsByDate);
        localSet(freezeKey(uid), nextFreeze);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));

    return () => { active = false; };
  }, [uid]);

  const addHabit = useCallback(
    async ({ name, icon, color }) => {
      const entry = {
        id: `habit_${Date.now()}`,
        name,
        icon: icon || '✅',
        color: color || '#10B981',
        createdAt: new Date().toISOString(),
        active: true,
      };
      setHabits((prev) => {
        const next = [...prev, entry];
        localSet(habitsKey(uid), next);
        return next;
      });
      if (uid) await fsSaveHabit(uid, entry).catch(() => {});
      return entry;
    },
    [uid]
  );

  const deleteHabit = useCallback(
    async (id) => {
      setHabits((prev) => {
        const next = prev.filter((h) => h.id !== id);
        localSet(habitsKey(uid), next);
        return next;
      });
      if (uid) await fsDeleteHabit(uid, id).catch(() => {});
    },
    [uid]
  );

  const toggleHabit = useCallback(
    (date, habitId) => {
      setLogsByDate((prev) => {
        const dayLog = prev[date] || {};
        const next = { ...prev, [date]: { ...dayLog, [habitId]: !dayLog[habitId] } };
        localSet(logsKey(uid), next);
        return next;
      });
      if (uid) {
        writeQueue.current = writeQueue.current
          .catch(() => {})
          .then(() => fsToggleHabitLog(uid, date, habitId));
      }
    },
    [uid]
  );

  const today = localDateKey();
  const weekDates = localDateKeys(7);
  const weekData = weekDates.map((date) => ({
    date,
    day: new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' }),
    log: logsByDate[date] || {},
    isToday: date === today,
  }));

  const getHabitStreak = useCallback(
    (habitId) => {
      const dates = Object.entries(logsByDate)
        .filter(([, log]) => log?.[habitId])
        .map(([date]) => date);
      return computeStreaks(dates, { frozenDates: freezeState.frozenDates });
    },
    [logsByDate, freezeState.frozenDates]
  );

  const todayLog = logsByDate[today] || {};
  const todayCompletionPct = habits.length
    ? Math.round((habits.filter((h) => todayLog[h.id]).length / habits.length) * 100)
    : 0;

  return {
    habits,
    weekData,
    loaded,
    addHabit,
    deleteHabit,
    toggleHabit,
    getHabitStreak,
    freezesAvailable: freezeState.freezesAvailable,
    todayCompletionPct,
  };
}
